<template>
  <div class="view-container">
    <div class="extended-filter-block">
      <h2>Заказы</h2>
      <div class="extended-search-block-mobile">
        <div class="search-outline-block">
          <SearchOutlined @click="showMobileFilterModal = true" style="color: rgba(0, 0, 0, 0.45)" />
          <div v-if="filterString" class="dot"></div>
        </div>
      </div>
    </div>
    <div class="card card__with-action my-orders-card">
      <h2>История заказов</h2>
      <div class="orders-control">
        <div class="segment-wrapper">
          <a-segmented v-model:value="currentFilter" :options="filterOptions" />
        </div>
        <div class="orders-action-buttons">
          <a-button class="secondary-button" @click="showOrderForm = true">
            <template #icon>
              <PlusOutlined />
            </template>
            Создать заказ
          </a-button>

          <a-button class="primary_button primary-button" @click="router.push('/catalog')">
            Посмотреть каталог
          </a-button>
        </div>
      </div>

      <div class="orders-table">
        <TableHorizontalScrollShell ref="tableShell">
          <a-table
            ref="tableRef"
            rowKey="id"
            :columns="columns"
            :data-source="tableData"
            :scroll="tableScroll"
            :pagination="false"
            :loading="tableLoading"
            :table-layout="'fixed'"
            @change="handleTableChange"
            :expandable="expandableConfig"
            :expandedRowKeys="expandedOrderRowKeys"
            :customRow="getOrderRowProps"
            @expand="handleOrderRowExpand"
        >
          <template #expandColumnTitle>
            <component :is="renderSettingsDropdown('bottomLeft')" />
          </template>

          <template #expandedRowRender="{ record }">
            <a-table
                class="inner-table"
                :columns="subColumns"
                :data-source="record.subproducts"
                :pagination="false"
                size="small"
                rowKey="key">

              <template #bodyCell="{column, text, record: subRecord }">
                <template v-if="column.dataIndex === 'sum'">
                  <span v-if="subRecord.price"> {{ subRecord.price * (subRecord.count ?? 1)}} </span>
                </template>

                <template v-if="column.dataIndex === 'activated_at'">
                  <span> {{ text ?? record.paid_at ?? '' }} </span>
                </template>

                <template v-if="column.dataIndex === 'type'">
                  <span> {{ productTypeDictionary[text] ?? 'Аналитика' }}</span>
                </template>
              </template>
            </a-table>
          </template>

          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'status'">
              <a-tag color="green" v-if="record.status === 'paid'">
                Оплачен
              </a-tag>
              <a-tag class="blue-tag" v-else>
                Не оплачен
              </a-tag>
            </template>

            <template v-if="column.dataIndex === 'price'">
              <span v-if="text"> {{ text }} &#8381;</span>
            </template>

            <template v-if="column.dataIndex === 'user'">
              <span v-if="text" class="user-link" @click="router.push('/staff/' + record.user_id + '/edit')"> {{ text }}</span>
            </template>
          </template>

          <template #emptyText>
            <div class="no-data-wrapper">
              <img :src="NotFoundIcon" class="no-data-image" alt="Нет результатов"/>

              <div class="no-data-text">
                {{
                  filterString
                      ? 'Нет данных для отображения. Измените фильтры или запрос.'
                      : 'Нет данных для отображения.'
                }}
              </div>

              <a-button
                  v-if="filterString"
                  @click="resetFilters"
              >
                <template #icon>
                  <ReloadOutlined />
                </template>
                Сбросить фильтры
              </a-button>
            </div>
          </template>
        </a-table>
        </TableHorizontalScrollShell>
      </div>

      <div class="pagination-block">
        <a-pagination v-model:current="currentPage"
                      :page-size="entitiesPerPage"
                      :buildOptionText="(count) => `${count.value} / стр`"
                      :show-total="isMobile ? undefined : (total => `Всего ${total ?? 0} элементов`)"
                      :total="totalOrders"
                      :show-less-items="isMobile"
                      @change="handlePageChange"
        />
      </div>
    </div>

    <SmallUsersFilterMobile
        v-if="showMobileFilterModal"
        :open="showMobileFilterModal"
        @close="showMobileFilterModal = false"
    />

    <NewOrderForm
        v-if="showOrderForm"
        :open="showOrderForm"
        :showOrdersButton="false"
        @close="showOrderForm = false"
        @submitted="fetchAfterSubmit"
    />
  </div>
</template>

<script>
import {onBeforeUnmount, ref, watch} from "@vue/runtime-core";
  import {computed} from "@vue/reactivity";
  import {h} from "vue";
import {ReloadOutlined, SettingOutlined} from "@ant-design/icons-vue";
  import {useStore} from "vuex";
  import {SearchOutlined, PlusOutlined} from "@ant-design/icons-vue";
import SmallUsersFilterMobile from "../../feature/users/forms/SmallUsersFilterMobile.vue";
import SubscriptionOptions from "../Subscription/SubscriptionOptions.vue";
import CurrentSubscriptionSummary from "../Subscription/CurrentSubscriptionSummary.vue";
import CreateOrders from "../Orders/CreateOrders.vue";
import OrderForm from "../../feature/order/create-order/OrderForm.vue";
import NewOrderForm from "../Orders/OrderForm.vue";
import NotFoundIcon from "@/assets/images/icons/no_data.svg";
import {useRouter} from "vue-router";
import useCheckRights from "@/mixins/useCheckRights";
import {useTableColumns} from "@/mixins/useTableColumns";
import TableHorizontalScrollShell from "@/components/Tables/TableScrollComponent.vue";
import useBreakpoints from "@/mixins/useBreakpoints";

export default {
    components: {
      TableHorizontalScrollShell,
      ReloadOutlined,
      NewOrderForm,
      OrderForm,
      CreateOrders,
      CurrentSubscriptionSummary, SubscriptionOptions, SmallUsersFilterMobile, SearchOutlined, PlusOutlined, SettingOutlined},
    setup() {
      const store = useStore();
      const router = useRouter();
      const showMobileFilterModal = ref(false);

      const entitiesPerPage = ref(10);
      const currentPage = ref(1);
      const tableLoading = ref(true);
      const showOptions = ref(false);
      const showOrderForm =ref(false);
      const { isMobile } = useBreakpoints();

      const currentSubscriptionSummary = computed(() => store.state.subscription.subscriptionSummary);
      const totalOrders = computed(() => store.state.relations.total);
      const orders = computed(() => store.state.relations.ordersList);
      const specificOrders = computed(() => store.state.relations.specificOrders);
      const tableData = ref();
      const expandedOrderRowKeys = ref([]);
      const {getRight} = useCheckRights();

      const filterOptions = [
        {
          label: 'Все',
          value: 'all',
        },
        {
          label: 'Новые',
          value: 'new',
        },
        {
          label: 'Оплачены',
          value: 'paid',
        }
      ];

      const currentFilter = ref('all');

      const filters = computed(() => store.state.users.filters);
      const filterString = computed(() => store.state.users.filterString);
      const user = computed(() => store.state.profile.profile);

      const resetFilters = () => {
        store.dispatch('users/setUserFilters', {
          filters: {
            name: '',
            job_title: '',
            department: '',
            online_from: '',
            created_at: '',
            email: '',
            telephone: '',
            groups: [],
            without_group_only: false,
            without_limits_only: false,
            level_from: null,
            level_to: null,
          },
          filterString: null,
        });

        currentPage.value = 1;
      }

      //сортировка
      const sortState = ref({
        field: null,
        order: null,
      });

      const handleTableChange = (pagination, filter, sorter) => {
        sortState.value = {
          field: sorter?.field ?? null,
          order: sorter?.order ?? null,
        };

        fetchOrders();
      }

      const fetchOrders = () => {
        tableLoading.value = true;

        store.dispatch("relations/getOrdersSummary", {
          status: currentFilter.value === 'all' ? null : currentFilter.value,
          per_page: entitiesPerPage.value,
          page: currentPage.value,
          special_orders: specificOrders.value?.length ? specificOrders.value : null,
          sort_field: sortState.value.field,
          sort_direction:
              sortState.value.order === 'ascend'
                  ? 'asc'
                  : sortState.value.order === 'descend'
                      ? 'desc'
                      : null,
          ...filters.value,
        }).then(() => {
          tableData.value = orders.value.map((el) => {
            return {
              id: el.id,
              created_at: el.created_at,
              status: el.status,
              user: el.user_info?.name ? el.user_info.name : (`${el.user.last_name} ${el.user.first_name} ${el.user.patronymic}`),
              email: el.user_info?.email ? el.user_info.email : el.user.email,
              user_id: el.user.id,
              paid_at: el.paid_at,
              price: el.price,
              subproducts: [
                ...(el.entities ?? []),
                ...(el.products ?? []),
                ...(el.subscriptions ?? []),
              ],
            }
          });
          const availableOrderIds = new Set(tableData.value.map((order) => order.id));
          expandedOrderRowKeys.value = expandedOrderRowKeys.value.filter((id) => availableOrderIds.has(id));
        }).finally(() => {
          tableLoading.value = false;
        });
      }

      const initialColumns = ref([
        {
          title: 'ID',
          dataIndex: 'id',
          width: 80,
          sorter: true,
          visible: true,
        },
        {
          title: 'Создан',
          dataIndex: 'created_at',
          sorter: true,
          visible: true,
          width: 250,
        },
        {
          title: 'Стоимость',
          dataIndex: 'price',
          sorter: true,
          visible: true,
          width: 100,
        },
        {
          title: 'Статус',
          dataIndex: 'status',
          visible: true,
          width: 100,
        },
        {
          title: 'Пользователь',
          dataIndex: 'user',
          sorter: false,
          visible: true,
          width: 220,
        },
        {
          title: 'Почта',
          dataIndex: 'email',
          sorter: false,
          visible: true,
          width: 270,
        },
        {
          title: 'Время оплаты',
          dataIndex: 'paid_at',
          sorter: true,
          visible: true,
          width: 150,
        },
      ]);

      const { columns, scrollX, scrollY, tableRef, tableScroll, updateTableYScroll, renderSettingsDropdown } = useTableColumns(initialColumns, {
        tableKey: 'orders',
        settingsWidth: 40,
        hideSettings: true,
        dataSource: tableData,
        scrollYSize: {
          percent: 100,
          offset: 360,
        },
      });

      const isOrderRowExpandable = (record) =>
          Array.isArray(record.subproducts) && record.subproducts.length > 0;

      const setOrderRowExpanded = (record, expanded) => {
        if (!isOrderRowExpandable(record)) {
          return;
        }

        const rowKey = record.id;
        const expandedKeys = expandedOrderRowKeys.value;

        if (expanded) {
          if (!expandedKeys.includes(rowKey)) {
            expandedOrderRowKeys.value = [...expandedKeys, rowKey];
          }

          return;
        }

        expandedOrderRowKeys.value = expandedKeys.filter((key) => key !== rowKey);
      }

      const handleOrderRowExpand = (expanded, record) => {
        setOrderRowExpanded(record, expanded);
      }

      const isInteractiveRowTarget = (event) => {
        return event.target?.closest?.([
          'a',
          'button',
          'input',
          'textarea',
          'select',
          'label',
          '[role="button"]',
          '[role="link"]',
          '.ant-btn',
          '.ant-checkbox-wrapper',
          '.ant-dropdown-trigger',
          '.ant-picker',
          '.ant-radio-wrapper',
          '.ant-select',
          '.ant-table-row-expand-icon',
          '.user-link',
        ].join(','));
      }

      const getOrderRowProps = (record) => ({
        class: isOrderRowExpandable(record) ? 'orders-table__row--expandable' : '',
        style: isOrderRowExpandable(record) ? { cursor: 'pointer' } : {},
        onClick: (event) => {
          if (isInteractiveRowTarget(event)) {
            return;
          }

          setOrderRowExpanded(record, !expandedOrderRowKeys.value.includes(record.id));
        },
      });

      const expandableConfig = computed(() => ({
        rowExpandable: isOrderRowExpandable,
        width: 20,
      }))

      const productTypeDictionary = {
        test: 'ТЭИ',
        tests: 'ТЭИ',
        course: 'Развитие',
        courses: 'Развитие',
        reference_profile: 'Эталонный Профиль',
        subscription: 'Подписка',
        tools: 'Инструменты',
      }

      const subColumns = [
        { title: 'Наименование', dataIndex: 'name', width: 150 },
        { title: 'Тип', dataIndex: 'type', width: 80 },
        { title: 'Кол-во', dataIndex: 'count', width: 90 },
        { title: 'Стоимость', dataIndex: 'price', width: 80 },
        { title: 'Сумма', dataIndex: 'sum', width: 120 },
        { title: 'Дата активации', dataIndex: 'activated_at', width: 140 },
        { title: 'Истекает', dataIndex: 'expires_at', width: 150 },
      ];

      const updateFilters = (clearSpecificOrders = true) => {
        if (clearSpecificOrders) {
          store.commit('relations/setSpecificOrders', []);
        }

        showMobileFilterModal.value = false;

        currentPage.value = 1;
        fetchOrders();
      }

      const handlePageChange = (current, perPage) => {
        if (entitiesPerPage.value !== perPage) {
          entitiesPerPage.value = perPage;
          setEntitiesPerPage(entitiesPerPage.value);
        }

        currentPage.value = current;
        fetchOrders();
      }

      const setEntitiesPerPage = (num) => {
        localStorage.setItem('perPage', num)
      }

      watch(() => user.value, () => {
            if (user.value?.company?.id) {
              store.dispatch('subscription/getSubscriptionSummary', user.value?.company?.id)
            }

            if (user.value?.id && getRight('staff')) {
              router.push('/personal')
            }
          },
          { deep: true, immediate: true }
      );

      const isInitialFiltersFetch = ref(true);

      watch(filters, () => {
            const clearSpecificOrders = !isInitialFiltersFetch.value;
            isInitialFiltersFetch.value = false;

            updateFilters(clearSpecificOrders);
          },
          { deep: true, immediate: true }
      );

      watch(() => currentFilter.value, () => {
        store.commit('relations/setSpecificOrders', []);
        fetchOrders();
      });

      onBeforeUnmount(() => {
        store.commit('relations/setSpecificOrders', []);
        store.dispatch('users/clearUserFilters');
      });

      const fetchAfterSubmit = () => {
        resetFilters();
      };

      const tableShell = ref(null);

      watch(tableLoading, async (loading) => {
        if (!loading) {
          await tableShell.value?.rebindScroll();
          await updateTableYScroll();
        }
      });

      watch(tableData, async () => {
        await tableShell.value?.rebindScroll();
        await updateTableYScroll();
      }, { deep: true });

      return {
        NotFoundIcon,
        router,
        showMobileFilterModal,
        filterString,
        currentSubscriptionSummary,
        showOptions,
        filterOptions,
        currentFilter,
        showOrderForm,
        fetchAfterSubmit,
        totalOrders,
        currentPage,
        entitiesPerPage,
        handleTableChange,
        handlePageChange,
        columns,
        tableData,
        resetFilters,
        subColumns,
        tableLoading,
        productTypeDictionary,
        expandableConfig,
        expandedOrderRowKeys,
        getOrderRowProps,
        handleOrderRowExpand,
        scrollX,
        scrollY,
        tableShell,
        tableRef,
        tableScroll,
        renderSettingsDropdown,
        isMobile
      }
    }
  }
</script>

<style scoped lang="scss">
  .view-container {
    padding-top: 0;
    margin-top: 0;
  }

  .extended-filter-block {
    display: none;

    @media (max-width: $mobile-breakpoint) {
      margin: 24px 20px;
      display: flex;
      column-gap: 25px;
      align-items: center;
      justify-content: space-between;
    }
  }

  .extended-filter-block h2 {
    font-size: 20px;
    margin-bottom: 0;
    font-weight: 600;
    line-height: 120%;
  }

  .extended-search-block-mobile {
    display: none;
    margin-right: 10px;

    @media(max-width: 768px) {
      display: block;
    }
  }

  :deep(.anticon-reload) {
    cursor: pointer;
  }

  :deep(.anticon-reload:hover) {
    color: var(--main-green) !important;
  }

  .orders-control {
    display: flex;
    justify-content: space-between;
    column-gap: 10px;
    margin-bottom: 16px;

    @media (max-width: $mobile-breakpoint) {
      flex-direction: column;
      gap: 16px;
    }
  }

  .orders-action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;

    @media (max-width: $mobile-breakpoint) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  .search-outline-block {
    position: relative;
  }

  .dot {
    position: absolute;
    top: -1px;
    left: 11px;
    height: 5px;
    width: 5px;
    background-color: var(--color-error);
    border-radius: 50%;
  }

  h2 {
    margin-bottom: 16px;
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
  }

  .pagination-block {
    margin-left: auto;
    margin-top: 20px;
  }

  .my-orders-card {
    :deep(.ant-table-wrapper:not(.inner-table) .ant-table-content) {
      min-height: calc(100vh - 330px);
      max-height: calc(100vh - 330px);
      overflow: auto !important;

      @media (max-width: $tablet-breakpoint) {
        max-height: unset;
      }
    }

    :deep(.ant-table-wrapper.inner-table .ant-table-content) {
      min-height: unset;
      max-height: unset;
      overflow: visible !important;
    }

    :deep(.ant-table-body) {
      min-height: calc(100vh - 350px);
      overflow: auto !important;

      @media (max-width: $tablet-breakpoint) {
        max-height: unset;
      }
    }
  }

  :deep(.ant-table-thead >tr>th) {
    padding: 5px 10px !important;
  }

  :deep(.ant-table-tbody>tr:not(.ant-table-measure-row)>td) {
    padding: 10px 5px !important;
  }

  :deep(.orders-table__row--expandable) {
    cursor: pointer;
  }

  .inner-table {
    background: #fafafa;

    :deep(.ant-table-tbody) {
      background: #fafafa;
    }

    :deep(.ant-table-wrapper .ant-table-tbody >tr >td >.ant-table-wrapper:only-child .ant-table) {
      margin-block: 0 !important;
    }

    :deep(.ant-table.ant-table-empty) {
      margin-block: 0 !important;
    }

    :deep(thead th) {
      color: var(--grey-text);
      border-bottom: unset;
    }

    :deep(.ant-table-thead > tr > th) {
      padding-left: 10px !important;
      padding-right: 10px !important;
    }

    :deep(.ant-table-tbody > tr > td) {
      padding-left: 10px !important;
      padding-right: 10px !important;
    }
  }

  .orders-table {
    position: relative;
  }

  .expand-header-gear {
    position: absolute;
    top: 8px;
    left: 10px;
    z-index: 1;
    font-size: 14px;
    color: var(--grey-text);
  }

  .user-link {
    cursor: pointer;
    color: var(--main-green);
  }

  .user-link:hover {
    color: var(--hover-color);
  }
</style>
